import { useQuery } from '@tanstack/react-query';
import type { LocationMatch } from '@citibox-panel/api-client';
import { locationSearchKey } from '../../../lib/locationSearchKey';
import { titularLocationId, titularLocationQuery } from '../lib/titularLocation';

const FIVE_MINUTES = 5 * 60 * 1000;

export type TitularLocation =
  | { status: 'pending' }
  | { status: 'resolved'; id: number }
  | { status: 'unresolved' };

// The ubicación the titular's stored address belongs to, for the address-mismatch warning: the
// address search ([AS-037]) asked for the titular's line, read by the row that names it whole.
// `pending` while that read is in flight, so the warning waits instead of showing and then going
// away; `unresolved` whenever there is no identity to compare — the caller has no delivery id to set
// it against (`enabled` false), no address, a term the server would reject, a failed read, or an
// answer that does not come down to one ubicación.
//
// Keyed like the header's address search and the ficha's combobox, so an answer either of them
// already holds is reused. Like useTitularAddress, a failure never throws: it only sends the warning
// back to the text comparison, and must never take the «Entrega» detail down with it.
export function useTitularLocation(
  titularAddress: string | null,
  searchLocations: (q: string) => Promise<LocationMatch[]>,
  enabled: boolean,
): TitularLocation {
  const term = titularAddress === null ? null : titularLocationQuery(titularAddress);
  const active = enabled && term !== null;
  const { data, isPending } = useQuery({
    queryKey: locationSearchKey(term ?? ''),
    queryFn: () => searchLocations(term ?? ''),
    enabled: active,
    staleTime: FIVE_MINUTES,
    throwOnError: false,
  });

  if (!active || titularAddress === null) return { status: 'unresolved' };
  if (data === undefined) return isPending ? { status: 'pending' } : { status: 'unresolved' };
  const id = titularLocationId(data, titularAddress);
  return id === null ? { status: 'unresolved' } : { status: 'resolved', id };
}
